import { ChevronRight, CornerDownLeft, X } from "lucide-react";
import { forwardRef, useId, type KeyboardEvent, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { DialogClose, DialogTitle } from "@/components/ui/dialog";
import { Kbd } from "@/components/ui/kbd";
import { Spinner } from "@/components/ui/spinner";
import { Tag } from "@/components/ui/tag";

type FrameProps = {
  /** The breadcrumb's last segment and the dialog's accessible name. */
  title?: string;
  firmName?: string | null;
  /** 0–100. */
  progress: number;
  hint?: ReactNode;
  onBack?: () => void;
  onCancel: () => void;
  primaryLabel: string;
  onPrimary: () => void;
  isPrimaryDisabled: boolean;
  isPrimaryLoading: boolean;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  children: ReactNode;
};

/**
 * The New Case and New Client modals' chrome, Linear's create modal: a thin progress bar, a
 * breadcrumb chip with close, the current question, then a footer with the
 * key hint, Cancel and the one primary action.
 */
export const NewCaseDialogFrame = forwardRef<HTMLDivElement, FrameProps>(
  (
    {
      title = "New Case",
      firmName,
      progress,
      hint,
      onBack,
      onCancel,
      primaryLabel,
      onPrimary,
      isPrimaryDisabled,
      isPrimaryLoading,
      onKeyDown,
      children
    },
    ref
  ) => (
    <div
      ref={ref}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex max-h-full min-h-0 flex-col outline-none"
    >
      <div className="h-0.5 shrink-0 bg-fill-2">
        <div
          className="h-full bg-fg transition-[width] duration-300"
          style={{ width: `${Math.max(progress, 4)}%` }}
        />
      </div>
      <div className="flex h-11 shrink-0 items-center gap-1.5 pr-2 pl-3.5">
        {firmName ? (
          <>
            <Tag>{firmName}</Tag>
            <ChevronRight className="size-3 text-fg-4" aria-hidden />
          </>
        ) : null}
        <DialogTitle className="text-base font-normal text-fg-2">
          {title}
        </DialogTitle>
        <span className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost" size="icon-xs" aria-label="Close">
            <X />
          </Button>
        </DialogClose>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-1 pb-4">
        {children}
      </div>
      <div className="flex h-13 shrink-0 items-center gap-2 border-t border-line-2 pr-3 pl-5">
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {hint}
        </span>
        {onBack ? (
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onPrimary}
          disabled={isPrimaryDisabled || isPrimaryLoading}
          aria-busy={isPrimaryLoading || undefined}
          data-icon="inline-end"
        >
          {primaryLabel}
          {isPrimaryLoading ? (
            <Spinner className="size-3.5" aria-hidden />
          ) : (
            <Kbd className="h-4 min-w-4 bg-primary-foreground/20 text-primary-foreground">
              <CornerDownLeft className="size-2.5" aria-label="Enter" />
            </Kbd>
          )}
        </Button>
      </div>
    </div>
  )
);
NewCaseDialogFrame.displayName = "NewCaseDialogFrame";

/** One question in the modal: the step number, the prompt, its answer area. */
export const DialogQuestion = forwardRef<
  HTMLElement,
  {
    stepNumber: number;
    title: ReactNode;
    description?: ReactNode;
    children: ReactNode;
  }
>(({ stepNumber, title, description, children }, ref) => {
  const titleId = useId();
  return (
    <section
      ref={ref}
      aria-labelledby={titleId}
      tabIndex={-1}
      className="flex flex-col gap-2.5 outline-none"
    >
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-fg-4 tabular-nums">{stepNumber}</span>
        <p id={titleId} className="text-lg font-medium text-fg">
          {title}
        </p>
      </div>
      {description ? (
        <p className="-mt-1 text-base text-fg-3">{description}</p>
      ) : null}
      {children}
    </section>
  );
});
DialogQuestion.displayName = "DialogQuestion";
